import type { ServiceFetch } from "../../services/serviceFetch";

/* ── Domain types (mirrors Engage API payload shape) ── */

export interface EngageSite {
  site?: string;
  display?: string;
  emails?: string;
}

export interface EngageJobType {
  joB_TYPE: string;
  title?: string;
  joB_TYPE_NAME?: string;
}

export interface EngageRequestor {
  organizationsid?: number | null;
  emaiL_ADDRESS?: string;
  firsT_NAME?: string;
  lasT_NAME?: string;
  // The DataModel response carries up to 20 field/value pairs that must be
  // forwarded as JobHeaderDTO entries on the create payload.
  [key: `prM${string}`]: unknown;
  [key: `vaL${string}`]: unknown;
}

export interface EngageJobAttachment {
  id: number;
  joB_NO: string;
  posteD_BY: string;
  session: string;
  comments: string;
  file: string;
  filE_NAME: string;
  filE_LOCATION: string;
  type: string;
  sorder: number;
  size: number;
  url: string;
  filE_TYPE: string;
  iS_SELECTED: boolean;
}

export interface EngageJobHeader {
  field: string;
  value: string;
}

/**
 * Write shape for POST /api/Requests/SaveAttachments — uppercase keys matching
 * Engage's JobAttachmentDTO. This JSON endpoint only supports **URL mode**:
 * `FILE_TYPE` must be the literal `"URL"` and `URL` the file's hosted
 * (OneDrive/SharePoint) location — Engage stores that URL as a link, it does
 * NOT fetch it. File mode (`FILE_TYPE:"FILE"`) reads the bytes from a multipart
 * `POSTED_FILE` stream that a JSON body cannot carry, so inline base64 in `FILE`
 * is never stored and any other `FILE_TYPE` fails the save (AIVA-6989).
 */
export interface EngageAttachmentUpload {
  JOB_NO: string;
  URL: string;
  FILE_NAME: string;
  FILE_LOCATION: string;
  FILE_TYPE: string;
  POSTED_BY: string;
  IS_SELECTED: boolean;
}

/**
 * Mirrors Engage's JobModelDTO (per /swagger/v1/swagger.json). Form fields
 * like the job title and instructions are sent as entries in `headers`,
 * not top-level keys — that's the contract the API validates against.
 */
export interface EngageJobModel {
  headers: EngageJobHeader[];
  categories?: unknown[];
  attachments?: EngageJobAttachment[];
  reason?: string;
  reasoN_CODE?: string;
  parenT_JOB_NO?: string;
  [key: string]: unknown;
}

export interface EngageJob {
  joB_NO?: string;
  job_no?: string;
  statuS_NAME?: string;
  status_name?: string;
  status?: string;
  stagE_NAME?: string;
  stage_name?: string;
  [key: string]: unknown;
}

export interface EngageCreateResponse {
  success?: boolean;
  // On success Engage returns an object with the job details; on validation
  // failure (status 200 + success:false) it returns a plain error string.
  result?: string | { joB_NO?: string; jobNo?: string; sla?: string; [k: string]: unknown };
  joB_NO?: string;
  jobNo?: string;
  [key: string]: unknown;
}

interface JsonRequest {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
}

/* ── Client factory ──────────────────────────────── */

function buildUrl(base: string, path: string): string {
  if (!base) {
    throw new Error(
      "Engage API base URL is not configured on the connection (apiBaseUrl).",
    );
  }
  return base.replace(/\/$/, "") + path;
}

export interface EngageClient {
  getSitesByUser(): Promise<EngageSite[]>;
  getJobTypesByUser(site: string): Promise<EngageJobType[]>;
  getRequestorByEmail(site: string, jobType: string, email: string): Promise<EngageRequestor[]>;
  createInternal(site: string, jobType: string, orgId: number | string, jobModel: EngageJobModel): Promise<EngageCreateResponse>;
  createFallback(user: string, site: string, jobType: string, jobModel: EngageJobModel): Promise<EngageCreateResponse>;
  saveAttachments(attachments: EngageAttachmentUpload[]): Promise<unknown>;
  getProcessing(site: string, emailFilter?: string): Promise<EngageJob[]>;
  getUserProcessing(): Promise<EngageJob[]>;
  getStatus(site: string, timestamp: string): Promise<EngageJob[]>;
  getJob(site: string, jobNo: string): Promise<EngageJob | null>;
  searchJob(jobNo: string): Promise<EngageJob | null>;
  callOperation(site: string, jobNo: string, operationId: string, body: unknown): Promise<unknown>;
}

export function createEngageClient(serviceFetch: ServiceFetch, apiBaseUrl: string): EngageClient {
  async function jsonCall<T>(path: string, init: JsonRequest = {}): Promise<T> {
    const method = init.method ?? "GET";
    const headers: Record<string, string> = {};
    let body: BodyInit | undefined;
    if (init.body !== undefined) {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(init.body);
    }
    const res = await serviceFetch("engage", {
      url: buildUrl(apiBaseUrl, path),
      method,
      headers,
      body,
    });
    if (!res.ok) {
      const errText = await res.text().catch(() => "");
      throw new Error(`Engage ${method} ${path}: ${res.status} ${errText}`);
    }
    const text = await res.text();
    return (text ? JSON.parse(text) : null) as T;
  }

  return {
    getSitesByUser: () => jsonCall<EngageSite[]>("/api/Sites/GetByUser"),

    getJobTypesByUser: (site) =>
      jsonCall<EngageJobType[]>(`/api/Sites/GetTypesByUser/${encodeURIComponent(site)}`),

    getRequestorByEmail: (site, jobType, email) =>
      jsonCall<EngageRequestor[]>(
        `/api/Data/GetRequestorByEmail/${encodeURIComponent(site)}/${encodeURIComponent(jobType)}/${email}`,
      ),

    createInternal: (site, jobType, orgId, jobModel) =>
      jsonCall<EngageCreateResponse>(
        `/api/Requests/CreateInternal/PLUGIN/${encodeURIComponent(site)}/${encodeURIComponent(jobType)}/${orgId}?sendNotification=true`,
        { method: "POST", body: jobModel },
      ),

    // Mirrors the prototype's fallback Create URL — note there is NO orgId
    // path segment here (that was a divergence in the old dead-code version).
    createFallback: (user, site, jobType, jobModel) =>
      jsonCall<EngageCreateResponse>(
        `/api/Requests/Create/${encodeURIComponent(user)}/${encodeURIComponent(site)}/${encodeURIComponent(jobType)}?submit=true&accept=true&sendNotification=true`,
        { method: "POST", body: jobModel },
      ),

    saveAttachments: (attachments) =>
      jsonCall<unknown>("/api/Requests/SaveAttachments", { method: "POST", body: attachments }),

    getProcessing: (site, emailFilter) => {
      const suffix = emailFilter ? `/${encodeURIComponent(emailFilter)}` : "";
      return jsonCall<EngageJob[]>(`/api/Requests/GetProcessing/${encodeURIComponent(site)}${suffix}`);
    },

    getUserProcessing: () => jsonCall<EngageJob[]>("/api/Requests/GetUserProcessing"),

    // Job status/action feed for a site since a timestamp — the REST endpoint
    // that surfaces completed jobs (GetUserProcessing is active-only). Returns
    // ONE row per status change ({ joB_NO, action, createD_BY, created, … }),
    // wrapped as { success, result: [...] } — so we unwrap `result`. `timestamp`
    // is an ISO date-time, e.g. "2026-01-01T00:00:00".
    getStatus: async (site, timestamp) => {
      const resp = await jsonCall<{ result?: EngageJob[] } | EngageJob[] | null>(
        `/api/Requests/Status/${encodeURIComponent(site)}/${encodeURIComponent(timestamp)}`,
      );
      if (Array.isArray(resp)) return resp;
      return Array.isArray(resp?.result) ? resp.result : [];
    },

    getJob: (site, jobNo) =>
      jsonCall<EngageJob | null>(`/api/Requests/Get/${encodeURIComponent(site)}/${encodeURIComponent(jobNo)}`),

    // Search the full job index by job number. Unlike GetJob/GetProcessing/Status,
    // the Search result carries joB_TITLE for ANY job — including finished ones —
    // so it's how we recover a completed job's title (AIVA-6988). `comparer: "13"`
    // is Engage's "equals" filter operator, mirroring the Postman collection.
    searchJob: async (jobNo) => {
      const resp = await jsonCall<{ result?: EngageJob[] } | EngageJob[] | null>(
        "/api/Requests/Search/",
        { method: "POST", body: { comparer: "13", field: "job_no", value: jobNo } },
      );
      let rows: EngageJob[] = [];
      if (Array.isArray(resp)) rows = resp;
      else if (Array.isArray(resp?.result)) rows = resp.result;
      return rows[0] ?? null;
    },

    callOperation: (site, jobNo, operationId, body) =>
      jsonCall<unknown>(
        `/api/Requests/${encodeURIComponent(operationId)}/${encodeURIComponent(site)}/${encodeURIComponent(jobNo)}`,
        { method: "POST", body },
      ),
  };
}
